import {
  Grid,
  Paper,
  Button,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import EditNoteIcon from "@mui/icons-material/EditNote";

import { useTheme } from "@mui/material/styles";

import DynamicExcelHelpModal from "../General/DynamicExcelHelpModal";
import {
  getAttachmentTypes,
  updateAttachmentType,
} from "../../services/AttachmentTypeService";
import { getSourceTypeDefinitions } from "../../services/AttachmentSourceTypeService";
import {
  uploadAttachmentGenerated,
  previewUploadAttachmentGeneratedLawyers,
} from "../../services/LegalFileAttachmentService";
import CardHeader from "../General/CardHeader";
import GlassCard from "../General/GlassCard";
import TemplateEditor from "../General/TemplateEditor";
import FileUploadBox from "../General/FileUploadBox";
import SourcePicker from "./SourcePicker";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import { useToast } from "../../context/ToastContext";
import LawyerSessionRequiredDialog from "../General/Dialog/LawyerSessionRequiredDialog";
import useLawyerSessionQueue from "../../hooks/useLawyerSessionQueue";

function SectionBlock({ icon, title, description, children }) {
  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 1.5,
        borderRadius: 3,
        background:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.04)"
            : "rgba(255,255,255,0.75)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border:
          theme.palette.mode === "dark"
            ? "1px solid rgba(255,255,255,0.08)"
            : "1px solid rgba(0,0,0,0.08)",
        mt: 1,
      })}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2, mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2.5,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(25,118,210,0.10)",
            color: "primary.main",
            border: "1px solid rgba(25,118,210,0.16)",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, fontSize: 15.5, lineHeight: 1.15 }}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>
      </Box>
      {children}
    </Paper>
  );
}

export default function UploadSacAttachment() {
  const { showToast } = useToast();
  const theme = useTheme();
  const fileExcelRef = useRef(null);
  const sessionQueue = useLawyerSessionQueue();

  const [loading, setLoading] = useState(false);

  const [typesAll, setTypesAll] = useState([]);
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);

  const [sourceDefinitions, setSourceDefinitions] = useState([]);

  const [selectedExcel, setSelectedExcel] = useState(null);
  const [templateHtml, setTemplateHtml] = useState("");
  const [selectedSources, setSelectedSources] = useState([]);

  const [openHelp, setOpenHelp] = useState(false);
  const [confirmSaveTemplateOpen, setConfirmSaveTemplateOpen] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const fetchTypes = async () => {
    try {
      const res = await getAttachmentTypes();
      const data = res?.data?.data ?? [];
      setTypesAll(data);
    } catch {
      setTypesAll([]);
      showToast("Error al cargar los tipos de escritos.", "error")
    }
  };

  const fetchSourceDefinitions = async () => {
    try {
      const res = await getSourceTypeDefinitions();
      setSourceDefinitions(res?.data?.data ?? []);
    } catch {
      setSourceDefinitions([]);
    }
  };

  useEffect(() => {
    fetchTypes();
    fetchSourceDefinitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const filtered = typesAll.filter((t) => t.allow_online_upload);
    setTypes(filtered);
    setSelectedType((prev) =>
      prev ? (filtered.find((t) => t.id === prev.id) ?? null) : null,
    );
  }, [typesAll]);

  useEffect(() => {
    if (selectedType) {
      setTemplateHtml(selectedType.generate_template || selectedType.text || "");
      const preconfigured = (selectedType.source_configs ?? []).map(
        (sc) => sc.source_type,
      );
      setSelectedSources(preconfigured);
    } else {
      setTemplateHtml("");
      setSelectedSources([]);
    }
  }, [selectedType]);

  const getSourceConfig = (sourceType) =>
    (selectedType?.source_configs ?? []).find((sc) => sc.source_type === sourceType) ?? null;

  const isSourceFixed = (sourceType) => {
    const cfg = getSourceConfig(sourceType);
    return cfg !== null && !cfg.is_user_selectable;
  };

  const toggleSource = (sourceType) => {
    if (isSourceFixed(sourceType)) return;
    setSelectedSources((prev) =>
      prev.includes(sourceType)
        ? prev.filter((s) => s !== sourceType)
        : [...prev, sourceType],
    );
  };

  const handleExcelSelect = (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xls", "xlsx"].includes(ext)) {
      showToast("El archivo Excel debe ser .xls o .xlsx", "info")
      return;
    }
    setSelectedExcel(file);
  };

  const handleRestoreTemplate = () => {
    if (selectedType) {
      setTemplateHtml(selectedType.generate_template || selectedType.text || "");
    }
  };

  const handleSaveTemplateAsOriginal = async () => {
    if (!selectedType) return;

    setSavingTemplate(true);
    try {
      const payload = {
        name: selectedType.name ?? "",
        display_name: selectedType.display_name ?? "",
        id_web: selectedType.id_web ?? "",
        text: selectedType.text ?? "",
        generate_title: selectedType.generate_title ?? "",
        generate_template: templateHtml,
        generate_engine: selectedType.generate_engine ?? "",
        allow_upload: selectedType.allow_upload ?? false,
        allow_multiple: selectedType.allow_multiple ?? false,
        allow_generate: selectedType.allow_generate ?? false,
        allow_online_upload: selectedType.allow_online_upload ?? false,
        requires_file: selectedType.requires_file ?? true,
        uses_lawsuit_file: selectedType.uses_lawsuit_file ?? false,
        use_for_notification_certificate:
          selectedType.use_for_notification_certificate ?? false,
        status: selectedType.status ?? "enabled",
      };

      await updateAttachmentType(selectedType.id, payload);

      setTypesAll((prev) =>
        prev.map((t) =>
          t.id === selectedType.id ? { ...t, generate_template: templateHtml } : t,
        ),
      );

      showToast("Plantilla original actualizada.", "success");
      setConfirmSaveTemplateOpen(false);
    } catch (error) {
      showToast(
        error.response?.data?.message ||
          "Error al actualizar la plantilla original.",
        "error",
      );
    } finally {
      setSavingTemplate(false);
    }
  };

  const hasTypesLoaded = typesAll.length > 0;
  const hasTypesForMode = types.length > 0;
  const blocked = hasTypesLoaded && !hasTypesForMode;

  // Fuentes seleccionadas que requieren un código adicional en columna 2 del Excel
  const paramSources = sourceDefinitions.filter(
    (src) =>
      selectedSources.includes(src.source_type) &&
      src.resolver_strategy === "legal_file_relation_param",
  );
  const col2Label =
    paramSources.length > 0
      ? `Código de ${paramSources[0].label}`
      : "Datos adicionales (opcional)";

  const canSubmit =
    !loading &&
    !sessionQueue.isPending &&
    hasTypesLoaded &&
    !blocked &&
    !!selectedType &&
    !!selectedExcel;

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("type_id", selectedType.id);
    formData.append("file", selectedExcel);
    formData.append("template_html", templateHtml);
    selectedSources.forEach((s) => formData.append("selected_sources[]", s));
    return formData;
  };

  const submitFile = async () => {
    setLoading(true);
    try {
      const res = await uploadAttachmentGenerated(buildFormData());

      if (res.data?.warnings?.length > 0) {
        showToast(
          "Atención, algunas filas fueron omitidas:\n• " +
            res.data.warnings.join("\n• "),
          "error"
        );
      }

      showToast(`${res.data.message}`, "success");
      setSelectedType(null);
      setSelectedExcel(null);
      setTemplateHtml("");
      if (fileExcelRef.current) fileExcelRef.current.value = "";
    } catch (error) {
      console.error(error);
      if (
        error.response?.data?.errors &&
        Array.isArray(error.response.data.errors)
      ) {
        showToast(
          error.response.data.message +
            "\n\n• " +
            error.response.data.errors.join("\n• "),
          "error"
        );
      } else {
        showToast(
          error.response?.data?.message ||
            "Error inesperado al procesar la solicitud.",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      await sessionQueue.checkAndRun(
        () => previewUploadAttachmentGeneratedLawyers(buildFormData()),
        submitFile
      );
    } catch (error) {
      console.error(error);
      showToast(
        error.response?.data?.message ||
          "Error inesperado al procesar la solicitud.",
        "error"
      );
    }
  };

  const warningMessage = !hasTypesLoaded
    ? "No hay tipos de escritos cargados en el sistema."
    : "No hay tipos habilitados para subida en línea a SAC.";

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>

        <GlassCard>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Ver requisitos del Excel" arrow>
              <HelpOutlineIcon
                sx={{
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  "&:hover": { color: theme.palette.primary.dark },
                }}
                onClick={() => setOpenHelp(true)}
              />
            </Tooltip>
          </Box>

          <CardHeader
            icon={<CloudUploadIcon />}
            title="Subida en Línea a SAC"
            description="Cargá escritos directamente al portal SAC de forma masiva desde un Excel. Recibirás una notificación al finalizar el proceso."
          />

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {(!hasTypesLoaded || blocked) && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">{warningMessage}</Alert>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={types}
                  getOptionLabel={(option) =>
                    option.display_name || option.name || ""
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Tipo de escrito" fullWidth />
                  )}
                  value={selectedType}
                  onChange={(_, newValue) => setSelectedType(newValue)}
                  disabled={!hasTypesLoaded || blocked}
                />
              </Grid>

              {selectedType && (
                <Grid size={{ xs: 12 }}>
                  <SourcePicker
                    sourceDefinitions={sourceDefinitions}
                    sourceConfigs={selectedType.source_configs ?? []}
                    selected={selectedSources}
                    onToggle={toggleSource}
                    getHint={(src, isSelected) =>
                      src.resolver_strategy === "legal_file_relation_param" && isSelected
                        ? "Código en col. 2 del Excel"
                        : null
                    }
                  />
                </Grid>
              )}

              {selectedType && (
                <Grid size={{ xs: 12 }}>
                  <SectionBlock
                    icon={<DescriptionOutlinedIcon />}
                    title="Plantilla del Escrito"
                    description="Personalizá el texto base de este envío masivo. Las etiquetas como [EXPEDIENTE] se llenarán automáticamente con los datos de cada causa."
                  >
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 1.2, flexWrap: "wrap" }}>
                      <Tooltip title="Guarda este texto como la plantilla original de este tipo de escrito, para que se use por defecto en las próximas cargas." arrow>
                        <span>
                          <Button
                            startIcon={<EditNoteIcon />}
                            onClick={() => setConfirmSaveTemplateOpen(true)}
                            variant="outlined"
                            color="primary"
                            size="small"
                            disabled={!templateHtml}
                            sx={{ borderRadius: 2.2, fontWeight: 800, textTransform: "none" }}
                          >
                            Editar plantilla original
                          </Button>
                        </span>
                      </Tooltip>
                      <Button
                        startIcon={<RefreshOutlinedIcon />}
                        onClick={handleRestoreTemplate}
                        variant="outlined"
                        size="small"
                        sx={{ borderRadius: 2.2, fontWeight: 800, textTransform: "none" }}
                      >
                        Restaurar plantilla original
                      </Button>
                    </Box>
                    <TemplateEditor value={templateHtml} onChange={setTemplateHtml} />
                  </SectionBlock>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <FileUploadBox
                  selectedFile={selectedExcel}
                  onFileSelect={handleExcelSelect}
                  accept=".xls,.xlsx"
                  placeholder="Seleccionar Excel (Códigos de Expediente)"
                  helperText="O arrastrá el Excel aquí (.xls, .xlsx)"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                  disabled={!canSubmit}
                >
                  {loading ? <CircularProgress size={24} /> : "Iniciar carga en SAC"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </GlassCard>
      </Grid>

      <DynamicExcelHelpModal
        open={openHelp}
        onClose={() => setOpenHelp(false)}
        title="Requisitos del archivo Excel"
        description="El archivo debe ser formato .xls o .xlsx."
        columns={["Código de Expediente", col2Label]}
        example={[
          "12345678",
          paramSources.length > 0 ? "S1/1234567" : "Datos adicionales",
        ]}
      />

      <ConfirmDialog
        open={confirmSaveTemplateOpen}
        onClose={() => setConfirmSaveTemplateOpen(false)}
        onConfirm={handleSaveTemplateAsOriginal}
        loading={savingTemplate}
        title="Editar plantilla original"
        description={`Este texto va a reemplazar la plantilla original de "${selectedType?.display_name || selectedType?.name || "este tipo de escrito"}". Se usará por defecto en todas las próximas cargas de este tipo.`}
        confirmText="Guardar como original"
        cancelText="Cancelar"
        confirmColor="primary"
      />

      <LawyerSessionRequiredDialog {...sessionQueue.dialogProps} />
    </Grid>
  );
}
